import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  AlertTriangle, ArrowLeft, ArrowRight, ExternalLink, Info, Loader2, RefreshCw, RotateCcw,
} from 'lucide-react';

/**
 * Site içi akış haritası — kullanıcı sitede sayfadan sayfaya nereye gidiyor.
 *
 * Backend: GET /api/site-flow/graph (GA4 `pageReferrer` x `pagePath`).
 * Sayfa çiftlerinin toplamıdır, oturum içi sıralı yol DEĞİLDİR — "Yöntem"
 * bölümünde açıkça yazıyor, oradaki uyarıları silme.
 *
 * Sadece Yataş Bedding'de görünür (App.js allowBrandIds).
 */

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

// Doğrulanmış tek renkli sıralı mavi rampa (açık zemin, 1 = en az, 5 = en çok).
const RAMP = ['#86b6ef', '#5598e7', '#2a78d6', '#1c5cab', '#104281'];
const ON_RAMP = ['#10233c', '#ffffff', '#ffffff', '#ffffff', '#ffffff'];
const ALERT = '#d6172b';

const nf = (n) => Math.round(Number(n) || 0).toLocaleString('tr-TR');
const pct = (a, b) => (b > 0 ? (a / b) * 100 : 0);
const pct1 = (a, b) => `${pct(a, b).toFixed(1).replace('.', ',')}%`;
const tl = (n) => `${Math.round(Number(n) || 0).toLocaleString('tr-TR')} ₺`;
const dTR = (s) => (s ? s.split('-').reverse().join('.') : '—');

// Akış diyagramındaki satırlar — huni sırası.
const ROWS = [
  ['home', 'search', 'landing_special', 'brand'],
  ['category'],
  ['product'],
  ['cart'],
  ['account'],
  ['checkout'],
];
const ROW_OF = {};
ROWS.forEach((r, i) => r.forEach((t) => { ROW_OF[t] = i; }));

function SectionHead({ eyebrow, title, children }) {
  return (
    <div className="mb-4">
      <h2 className="text-[12px] font-bold uppercase tracking-wider text-blue-700">{eyebrow}</h2>
      <p className="mt-1.5 text-[22px] font-bold leading-snug tracking-tight text-slate-900">{title}</p>
      {children ? <p className="mt-1.5 max-w-[80ch] text-[13.5px] leading-relaxed text-slate-500">{children}</p> : null}
    </div>
  );
}

/* ── Ticari huni ─────────────────────────────────────────────────────────── */
function Funnel({ summary }) {
  const s = summary || {};
  const steps = [
    { lbl: 'Oturum', val: s.sessions, ramp: RAMP[0] },
    { lbl: 'Sepete ekleme', val: s.addToCarts, ramp: RAMP[1] },
    { lbl: 'Ödemeye başlama', val: s.checkouts, ramp: RAMP[2] },
    { lbl: 'Sipariş', val: s.purchases, ramp: RAMP[3] },
  ];
  // En dar boğazı iddia etme, sayıdan bul.
  let worst = null;
  for (let i = 1; i < steps.length; i += 1) {
    const r = pct(steps[i].val, steps[i - 1].val);
    if (!worst || r < worst.rate) worst = { rate: r, from: steps[i - 1], to: steps[i] };
  }
  return (
    <>
      <p className="mb-3 max-w-[80ch] text-[13.5px] leading-relaxed text-slate-500">
        Oturumdan siparişe dönüşüm <b className="text-slate-700">{pct1(s.purchases, s.sessions)}</b>.
        {worst ? (
          <>
            {' '}Oransal olarak en dar boğaz{' '}
            <b className="text-slate-700">{worst.from.lbl.toLocaleLowerCase('tr')} → {worst.to.lbl.toLocaleLowerCase('tr')}</b>:
            yalnızca {worst.rate.toFixed(1).replace('.', ',')}%’i geçiyor, {nf(worst.from.val - worst.to.val)} burada düşüyor.
          </>
        ) : null}
        {' '}Çubuklar mutlak sayıyı değil, <b className="text-slate-700">bir önceki adımdan geçme oranını</b> gösterir.
      </p>
      <div className="grid gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-5">
        {steps.map((st, i) => {
          const prev = i > 0 ? steps[i - 1] : null;
          const rate = prev ? pct(st.val, prev.val) : 100;
          return (
            <div key={st.lbl} className="bg-white px-4 py-3.5">
              <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-400">{st.lbl}</div>
              <div className="mt-0.5 text-[25px] font-bold leading-tight tabular-nums text-slate-900">{nf(st.val)}</div>
              <div className="mt-0.5 text-[12px] leading-snug text-slate-500">
                {prev ? (
                  <>
                    {prev.lbl} adımının <b className="tabular-nums text-slate-700">{pct1(st.val, prev.val)}</b>’i
                    <br />
                    <span className="font-semibold tabular-nums" style={{ color: ALERT }}>
                      −{nf(prev.val - st.val)} burada düşüyor
                    </span>
                  </>
                ) : (
                  `oturum başına ${(s.views / (s.sessions || 1)).toFixed(1).replace('.', ',')} sayfa görüntülenme`
                )}
              </div>
              <div className="mt-2 h-1 rounded-full" style={{ width: `${Math.max(2, rate)}%`, background: st.ramp }} />
            </div>
          );
        })}
        <div className="bg-white px-4 py-3.5">
          <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-400">Ciro</div>
          <div className="mt-0.5 text-[25px] font-bold leading-tight tabular-nums text-slate-900">{tl(s.revenue)}</div>
          <div className="mt-0.5 text-[12px] text-slate-500">sipariş başına {tl(s.revenue / (s.purchases || 1))}</div>
        </div>
      </div>
    </>
  );
}

/* ── Giriş noktası dağılımı ──────────────────────────────────────────────── */
function LandingSplit({ typeTotals, typeLabels }) {
  const rows = Object.entries(typeTotals || {})
    .map(([k, v]) => ({ k, label: typeLabels[k] || k, s: v.landingSessions }))
    .filter((r) => r.s > 0)
    .sort((a, b) => b.s - a.s);
  const total = rows.reduce((a, r) => a + r.s, 0);
  if (!total) return null;
  const cells = rows.slice(0, 4).concat([{ k: 'rest', label: 'Diğer', s: rows.slice(4).reduce((a, r) => a + r.s, 0) }]);
  const step = [4, 3, 2, 1, 0]; // koyudan açığa
  const home = rows.find((r) => r.k === 'home');

  return (
    <>
      <p className="mb-3 max-w-[80ch] text-[13.5px] leading-relaxed text-slate-500">
        {home ? (
          <>
            Oturumların yalnızca <b className="text-slate-700">{pct1(home.s, total)}</b>’i anasayfadan başlıyor.
            Trafiğin çoğunluğu reklam ve organik aramayla{' '}
            <b className="text-slate-700">doğrudan kategori ve ürün sayfalarına</b> iniyor — anasayfa sitenin kapısı değil, bir ara durak.
          </>
        ) : 'Oturumların hangi sayfa tipinde başladığı.'}
      </p>
      <div className="flex h-11 gap-0.5 overflow-hidden rounded-lg border border-slate-200 bg-slate-200">
        {cells.map((r, i) => {
          const w = pct(r.s, total);
          return (
            <div
              key={r.k}
              className="flex items-center justify-center overflow-hidden whitespace-nowrap text-[11.5px]"
              style={{ flex: `${w} 0 0`, background: RAMP[step[i]], color: ON_RAMP[step[i]] }}
              title={`${r.label} — ${nf(r.s)} oturum`}
            >
              {w > 8 ? `${r.label} ${w.toFixed(0)}%` : (w > 4 ? `${w.toFixed(0)}%` : '')}
            </div>
          );
        })}
      </div>
      <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1.5 text-[12.5px] text-slate-600">
        {cells.map((r, i) => (
          <span key={r.k} className="inline-flex items-center gap-2">
            <i className="h-2.5 w-2.5 flex-none rounded-sm" style={{ background: RAMP[step[i]] }} />
            {r.label} <b className="tabular-nums text-slate-800">{nf(r.s)}</b> oturum · {pct1(r.s, total)}
          </span>
        ))}
      </div>
    </>
  );
}

/* ── Katmanlı akış diyagramı ─────────────────────────────────────────────── */
function FlowDiagram({ data }) {
  const [hover, setHover] = useState(null);
  const TL = data.meta.typeLabels;

  const layout = useMemo(() => {
    const W = 1120; const PAD = 22; const CARD_H = 96; const PITCH = 128; const GAP = 24;
    const WIDE = 760; const NARROW = Math.round((WIDE - GAP * 3) / 4);
    const H = PAD * 2 + (ROWS.length - 1) * PITCH + CARD_H;

    const node = {};
    ROWS.forEach((row, ri) => {
      const w = row.length > 1 ? NARROW : WIDE;
      const total = w * row.length + GAP * (row.length - 1);
      let x = (W - total) / 2;
      row.forEach((t) => {
        node[t] = { t, x, y: PAD + ri * PITCH, w, h: CARD_H, cx: x + w / 2 };
        x += w + GAP;
      });
    });

    // Yalnızca aşağı yönlü (ileri) akış çizilir; geri dönüşler ayrı panelde.
    const fwd = data.typeEdges
      .filter((e) => ROW_OF[e.from] !== undefined && ROW_OF[e.to] !== undefined && ROW_OF[e.to] > ROW_OF[e.from])
      .sort((a, b) => b.views - a.views);
    if (!fwd.length) return { W, H, node, ribbons: [], outTotal: {} };

    const maxE = fwd[0].views;
    const wid = (v) => Math.max(1.6, (v / maxE) * 60);

    // Uç noktaları kart kenarına yay (Sankey mantığı) — üst üste binmesin.
    const outs = {}; const ins = {};
    fwd.forEach((e) => {
      (outs[e.from] = outs[e.from] || []).push(e);
      (ins[e.to] = ins[e.to] || []).push(e);
    });
    Object.keys(outs).forEach((k) => outs[k].sort((a, b) => node[a.to].cx - node[b.to].cx));
    Object.keys(ins).forEach((k) => ins[k].sort((a, b) => node[a.from].cx - node[b.from].cx));

    const anchor = {};
    Object.keys(outs).forEach((k) => {
      let x = node[k].cx - outs[k].reduce((a, e) => a + wid(e.views), 0) / 2;
      outs[k].forEach((e) => { const w = wid(e.views); anchor[`${e.from}>${e.to}:o`] = x + w / 2; x += w; });
    });
    Object.keys(ins).forEach((k) => {
      let x = node[k].cx - ins[k].reduce((a, e) => a + wid(e.views), 0) / 2;
      ins[k].forEach((e) => { const w = wid(e.views); anchor[`${e.from}>${e.to}:i`] = x + w / 2; x += w; });
    });

    const ribbons = fwd.map((e) => {
      const a = node[e.from]; const b = node[e.to];
      const x1 = anchor[`${e.from}>${e.to}:o`]; const y1 = a.y + a.h;
      const x2 = anchor[`${e.from}>${e.to}:i`]; const y2 = b.y;
      const dy = (y2 - y1) * 0.5;
      const ratio = e.views / maxE;
      return {
        key: `${e.from}>${e.to}`,
        d: `M${x1},${y1} C${x1},${y1 + dy} ${x2},${y2 - dy} ${x2},${y2}`,
        w: wid(e.views),
        color: ratio > 0.4 ? RAMP[3] : (ratio > 0.1 ? RAMP[2] : RAMP[1]),
        ...e,
      };
    });

    const outTotal = {};
    data.typeEdges.forEach((e) => { outTotal[e.from] = (outTotal[e.from] || 0) + e.views; });
    return { W, H, node, ribbons, outTotal };
  }, [data]);

  const topPages = useMemo(() => {
    const m = {};
    data.pages.forEach((p) => { (m[p.t] = m[p.t] || []).push(p); });
    Object.keys(m).forEach((k) => m[k].sort((a, b) => b.v - a.v));
    return m;
  }, [data]);

  const tt = data.typeTotals;

  return (
    <div className="relative overflow-x-auto rounded-xl border border-slate-200 bg-white px-4 pb-3 pt-5">
      <svg viewBox={`0 0 ${layout.W} ${layout.H}`} className="block h-auto w-full min-w-[780px]" role="img" aria-label="Sayfa tipleri arası ileri akış diyagramı">
        {layout.ribbons.map((r) => (
          <path
            key={r.key}
            d={r.d}
            fill="none"
            stroke={r.color}
            strokeWidth={r.w}
            opacity={hover && hover.key !== r.key ? 0.18 : 0.62}
            className="cursor-pointer transition-opacity"
            onMouseEnter={() => setHover(r)}
            onMouseLeave={() => setHover(null)}
          />
        ))}
        {Object.keys(layout.node).map((t) => {
          const n = layout.node[t];
          const d = tt[t] || { views: 0, pages: 0 };
          const wide = n.w >= 400;
          const list = topPages[t] || [];
          return (
            <g key={t}>
              <rect x={n.x} y={n.y} width={n.w} height={n.h} rx="9" fill="#ffffff" stroke="#cbd5e1" strokeWidth="1" />
              <text x={n.x + 15} y={n.y + 22} fontSize="10.5" letterSpacing="1.3" fill="#64748b" fontWeight="600">
                {(TL[t] || t).toLocaleUpperCase('tr')}
              </text>
              <text x={n.x + 15} y={n.y + 52} fontSize="22" fontWeight="700" fill="#0f172a" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {nf(d.views)}
              </text>
              <text x={n.x + 15} y={n.y + 71} fontSize="11" fill="#94a3b8">
                görüntülenme · {nf(d.pages)} sayfa
              </text>
              {wide ? (
                <>
                  {list.slice(0, 3).map((p, i) => {
                    const maxCh = Math.floor((n.w - 215) / 6.15);
                    const nm = p.l.length > maxCh ? `${p.l.slice(0, maxCh - 1)}…` : p.l;
                    return (
                      <text key={p.p} x={n.x + n.w - 14} y={n.y + 28 + i * 19} textAnchor="end" fontSize="11.5" fill="#475569">
                        {nm} <tspan fill="#94a3b8" style={{ fontVariantNumeric: 'tabular-nums' }}>{nf(p.v)}</tspan>
                      </text>
                    );
                  })}
                  <text x={n.x + n.w - 14} y={n.y + 86} textAnchor="end" fontSize="9.5" fill="#94a3b8" letterSpacing="0.9" fontWeight="600">
                    EN ÇOK GÖRÜNTÜLENEN SAYFALAR
                  </text>
                </>
              ) : (
                <text x={n.x + 15} y={n.y + 88} fontSize="10.5" fill="#475569">
                  {(() => {
                    const maxCh = Math.floor((n.w - 30) / 5.9);
                    const line = list.slice(0, 3).map((p) => p.l).join(' · ');
                    return line.length > maxCh ? `${line.slice(0, maxCh - 1)}…` : line;
                  })()}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      <div className="mt-1 flex flex-wrap items-center gap-x-6 gap-y-2 px-1 pb-1 text-[12.5px] text-slate-500">
        <span className="inline-flex items-center gap-2">
          <svg width="30" height="9"><line x1="0" y1="4.5" x2="30" y2="4.5" stroke={RAMP[3]} strokeWidth="8" opacity="0.62" /></svg> yoğun geçiş
        </span>
        <span className="inline-flex items-center gap-2">
          <svg width="30" height="9"><line x1="0" y1="4.5" x2="30" y2="4.5" stroke={RAMP[2]} strokeWidth="4" opacity="0.62" /></svg> orta
        </span>
        <span className="inline-flex items-center gap-2">
          <svg width="30" height="9"><line x1="0" y1="4.5" x2="30" y2="4.5" stroke={RAMP[1]} strokeWidth="1.6" opacity="0.62" /></svg> seyrek
        </span>
        {hover ? (
          <span className="font-medium text-slate-800">
            {TL[hover.from]} → {TL[hover.to]}: <b className="tabular-nums">{nf(hover.views)}</b> geçiş
            {' '}({TL[hover.from]} sayfalarından ölçülen tüm çıkışların {pct1(hover.views, layout.outTotal[hover.from])}’i)
          </span>
        ) : (
          <span>Şeridin üzerine gel — o geçişin gerçek sayısı burada çıkar.</span>
        )}
      </div>
    </div>
  );
}

/* ── Geri dönüş / katman içi ─────────────────────────────────────────────── */
function EdgeList({ title, note, rows, typeLabels }) {
  if (!rows.length) return null;
  const mx = Math.max(...rows.map((r) => r.views));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <h3 className="text-[13px] font-semibold text-slate-800">{title}</h3>
      <p className="mb-3 mt-0.5 text-[12.5px] leading-snug text-slate-500">{note}</p>
      <div className="flex flex-col gap-2.5">
        {rows.map((r) => (
          <div key={`${r.from}-${r.to}`}>
            <div className="flex items-baseline justify-between gap-3 text-[13.5px]">
              <span className="text-slate-600">
                {typeLabels[r.from]}
                {r.from === r.to
                  ? <span className="text-slate-400"> ↻ kendi içinde</span>
                  : <> <span style={{ color: ALERT }}>↑</span> {typeLabels[r.to]}</>}
              </span>
              <span className="font-semibold tabular-nums text-slate-900">{nf(r.views)}</span>
            </div>
            <div className="mt-1 h-1.5 rounded-full" style={{ width: `${pct(r.views, mx).toFixed(1)}%`, background: RAMP[2] }} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Sayfa gezgini ───────────────────────────────────────────────────────── */
function HopRow({ page, views, denom, onClick }) {
  const share = pct(views, denom);
  return (
    <button
      type="button"
      onClick={onClick}
      className="-mx-2 w-full rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="truncate text-[13.5px] text-slate-800">{page.l}</span>
        <span className="flex flex-none items-baseline gap-3">
          <span className="text-[13px] font-semibold tabular-nums text-slate-900">{nf(views)}</span>
          <span className="w-12 text-right text-[12px] tabular-nums text-slate-400">{pct1(views, denom)}</span>
        </span>
      </div>
      <div className="truncate font-mono text-[10.5px] text-slate-400">{page.p}</div>
      <div
        className="mt-1 h-1.5 rounded-full"
        style={{ width: `${Math.max(1.5, share).toFixed(1)}%`, background: share > 12 ? RAMP[3] : share > 4 ? RAMP[2] : RAMP[1] }}
      />
    </button>
  );
}

function Explorer({ data, adj }) {
  const { pages, meta } = data;
  const homeIdx = useMemo(() => {
    const i = pages.findIndex((p) => p.t === 'home');
    return i >= 0 ? i : 0;
  }, [pages]);
  const [trail, setTrail] = useState([homeIdx]);
  useEffect(() => { setTrail([homeIdx]); }, [homeIdx]);

  const cur = trail[trail.length - 1];
  const p = pages[cur];
  const push = (i) => setTrail((t) => (t[t.length - 1] === i ? t : [...t, i].slice(-12)));

  const [q, setQ] = useState('');
  const matches = useMemo(() => {
    const s = q.trim().toLocaleLowerCase('tr');
    if (s.length < 2) return [];
    return pages
      .map((pg, i) => ({ pg, i }))
      .filter(({ pg }) => pg.l.toLocaleLowerCase('tr').includes(s) || pg.p.includes(s))
      .slice(0, 8);
  }, [q, pages]);

  if (!p) return null;
  const ins = (adj.IN[cur] || []).slice(0, 10);
  const outs = (adj.OUT[cur] || []).slice(0, 12);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-start gap-2">
        <div className="relative min-w-[260px] flex-1">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Sayfa ara — örn. Yatak, Sepet, Nevresim, /tr/cart"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-[14px] outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />
          {matches.length > 0 && (
            <div className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
              {matches.map(({ pg, i }) => (
                <button
                  key={pg.p}
                  type="button"
                  onClick={() => { setTrail([i]); setQ(''); }}
                  className="block w-full px-3 py-2 text-left hover:bg-slate-50"
                >
                  <div className="text-[13.5px] text-slate-800">{pg.l}</div>
                  <div className="font-mono text-[10.5px] text-slate-400">{pg.p}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setTrail([homeIdx])}
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-2 text-[12.5px] text-slate-600 hover:border-blue-500 hover:text-blue-700"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Anasayfaya dön
        </button>
      </div>

      <div className="flex min-h-[22px] flex-wrap items-center gap-1.5 text-[12.5px] text-slate-400">
        {trail.length < 2 ? (
          <span>İz: tıkladıkça burada birikir.</span>
        ) : (
          <>
            <span>İz:</span>
            {trail.map((t, k) => (
              <React.Fragment key={`${t}-${k}`}>
                <button
                  type="button"
                  onClick={() => setTrail(trail.slice(0, k + 1))}
                  className="text-blue-700 underline underline-offset-2 hover:text-blue-900"
                >
                  {pages[t].l}
                </button>
                {k < trail.length - 1 ? <span>→</span> : null}
              </React.Fragment>
            ))}
          </>
        )}
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4 rounded-xl border border-slate-200 bg-white px-5 py-4">
        <div className="min-w-0">
          <div className="text-[23px] font-bold leading-tight tracking-tight text-slate-900">{p.l}</div>
          <a
            href={`${meta.siteBase}${p.p}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 break-all font-mono text-[12px] text-slate-400 hover:text-blue-700"
          >
            {p.p} <ExternalLink className="h-3 w-3 flex-none" />
          </a>
        </div>
        <div className="flex flex-wrap gap-6">
          {[
            ['Tip', meta.typeLabels[p.t] || p.t, true],
            ['Görüntülenme', nf(p.v)],
            ['Ölçülen giriş', nf(p.ki)],
            ['Ölçülen çıkış', nf(p.ko)],
          ].map(([k, v, small]) => (
            <div key={k}>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{k}</div>
              <div className={`font-bold tabular-nums text-slate-900 ${small ? 'text-[15px]' : 'text-[19px]'}`}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="mb-2.5 flex items-center justify-between gap-3 text-[12.5px] font-semibold text-slate-700">
            <span className="inline-flex items-center gap-1.5"><ArrowLeft className="h-3.5 w-3.5" /> Buraya nereden geldiler</span>
            <span className="font-normal tabular-nums text-slate-400">{nf(p.ki)} ölçülen giriş</span>
          </h3>
          {ins.length ? ins.map(([j, v]) => (
            <HopRow key={j} page={pages[j]} views={v} denom={p.ki || 1} onClick={() => push(j)} />
          )) : (
            <p className="py-1.5 text-[13px] text-slate-400">
              Bu sayfaya ölçülmüş site içi giriş yok — ziyaretçiler buraya doğrudan reklam/arama sonucuyla iniyor.
            </p>
          )}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="mb-2.5 flex items-center justify-between gap-3 text-[12.5px] font-semibold text-slate-700">
            <span className="inline-flex items-center gap-1.5">Buradan nereye gittiler <ArrowRight className="h-3.5 w-3.5" /></span>
            <span className="font-normal tabular-nums text-slate-400">{nf(p.ko)} ölçülen çıkış</span>
          </h3>
          {outs.length ? outs.map(([j, v]) => (
            <HopRow key={j} page={pages[j]} views={v} denom={p.ko || 1} onClick={() => push(j)} />
          )) : (
            <p className="py-1.5 text-[13px] text-slate-400">Bu sayfadan ölçülmüş site içi geçiş yok.</p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Sıralı kategori listesi (ortak gövde) ───────────────────────────────── */
// Uzun listeyi (200+ kategori) tek yerde yönetir: arama, ilk-N/tümü, çubuk.
function RankedList({ rows, total, siteBase, valueLabel, rightCol, emptyNote, initial = 20 }) {
  const [q, setQ] = useState('');
  const [all, setAll] = useState(false);

  const filtered = useMemo(() => {
    const s = q.trim().toLocaleLowerCase('tr');
    if (!s) return rows;
    return rows.filter((r) => r.l.toLocaleLowerCase('tr').includes(s) || r.p.includes(s));
  }, [q, rows]);

  const shown = all ? filtered : filtered.slice(0, initial);
  const mx = rows.length ? rows[0].val : 1;

  if (!rows.length) return <p className="rounded-xl border border-slate-200 bg-white p-5 text-[13.5px] text-slate-500">{emptyNote}</p>;

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Kategori ara — örn. Nevresim, Yastık, Baza"
          className="min-w-[220px] flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-[13.5px] outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
        />
        <span className="text-[12px] tabular-nums text-slate-400">
          {q ? `${nf(filtered.length)} / ` : ''}{nf(rows.length)} kategori
        </span>
      </div>

      <div className="divide-y divide-slate-100">
        {shown.map((r, i) => (
          <div key={r.p} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50">
            <span className="w-7 flex-none text-right text-[12px] tabular-nums text-slate-300">{i + 1}</span>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-3">
                <span className="truncate text-[13.5px] text-slate-800">{r.l}</span>
                <span className="flex flex-none items-baseline gap-3">
                  <span className="text-[13.5px] font-semibold tabular-nums text-slate-900">{nf(r.val)}</span>
                  <span className="w-12 text-right text-[12px] tabular-nums text-slate-400">{pct1(r.val, total || 1)}</span>
                  {rightCol ? <span className="w-24 text-right text-[12px] tabular-nums text-slate-500">{rightCol(r)}</span> : null}
                </span>
              </div>
              <a
                href={`${siteBase}${r.p}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex max-w-full items-center gap-1 truncate font-mono text-[10.5px] text-slate-400 hover:text-blue-700"
              >
                {r.p} <ExternalLink className="h-2.5 w-2.5 flex-none" />
              </a>
              <div
                className="mt-1 h-1.5 rounded-full"
                style={{
                  width: `${Math.max(1.2, pct(r.val, mx)).toFixed(1)}%`,
                  background: pct(r.val, mx) > 55 ? RAMP[3] : pct(r.val, mx) > 20 ? RAMP[2] : RAMP[1],
                }}
              />
            </div>
          </div>
        ))}
        {!shown.length ? <p className="px-4 py-4 text-[13px] text-slate-400">Aramayla eşleşen kategori yok.</p> : null}
      </div>

      {filtered.length > initial ? (
        <div className="border-t border-slate-100 px-4 py-2.5">
          <button
            type="button"
            onClick={() => setAll((v) => !v)}
            className="text-[12.5px] font-medium text-blue-700 underline underline-offset-2 hover:text-blue-900"
          >
            {all ? `İlk ${initial} tanesini göster` : `Tümünü göster — ${nf(filtered.length)} kategori`}
          </button>
          <span className="ml-2 text-[12px] text-slate-400">{valueLabel}</span>
        </div>
      ) : null}
    </div>
  );
}

/* ── Sayfa ───────────────────────────────────────────────────────────────── */
export default function SiteFlowMap() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) }),
    [brandId]
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(28);

  const load = useCallback(async (refresh = false) => {
    if (!brandId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/site-flow/graph?days=${days}${refresh ? '&refresh=1' : ''}`, {
        credentials: 'include', headers,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Akış haritası yüklenemedi');
      setData(json);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [brandId, headers, days]);

  useEffect(() => { load(); }, [load]);

  // Kenarları düğüm başına indeksle — gezgin her tıklamada bunu okur.
  const adj = useMemo(() => {
    if (!data) return { IN: [], OUT: [] };
    const OUT = data.pages.map(() => []);
    const IN = data.pages.map(() => []);
    data.edges.forEach(([a, b, v]) => { OUT[a].push([b, v]); IN[b].push([a, v]); });
    OUT.forEach((a) => a.sort((x, y) => y[1] - x[1]));
    IN.forEach((a) => a.sort((x, y) => y[1] - x[1]));
    return { IN, OUT };
  }, [data]);

  const backEdges = useMemo(() => (data ? data.typeEdges.filter(
    (e) => ROW_OF[e.from] !== undefined && ROW_OF[e.to] !== undefined && ROW_OF[e.to] < ROW_OF[e.from]
  ).slice(0, 7) : []), [data]);
  const selfEdges = useMemo(() => (data ? data.typeEdges.filter(
    (e) => e.from === e.to && ROW_OF[e.from] !== undefined
  ).slice(0, 6) : []), [data]);

  // 05 — Anasayfa → kategori
  const homeCatRows = useMemo(
    () => (data?.homeToCategory?.items || []).map((r) => ({ ...r, val: r.v })),
    [data]
  );

  // 06 — Kategoriden sepete: modellenmiş atıf mı, doğrudan ölçüm mü
  const [cartMode, setCartMode] = useState('modeled');
  const cartRows = useMemo(() => {
    const c = data?.categoryToCart;
    if (!c) return [];
    return cartMode === 'modeled'
      ? (c.modeled?.items || []).map((r) => ({ ...r, val: r.atc }))
      : (c.direct?.items || []).map((r) => ({ ...r, val: r.toCart }));
  }, [data, cartMode]);

  if (!brandId) {
    return <div className="p-8 text-slate-500">Marka seçili değil.</div>;
  }

  const cov = data?.meta?.coverage;

  return (
    <div className="mx-auto max-w-[1240px] px-5 py-7">
      <div className="mb-6 border-b border-slate-200 pb-5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] uppercase tracking-wider text-slate-400">
          <span>{data?.meta?.brandName || 'Marka'}</span><span>·</span>
          <span>{dTR(data?.meta?.start)} – {dTR(data?.meta?.end)}</span><span>·</span>
          <span>GA4 property <b className="font-normal text-blue-700">{data?.meta?.propertyId || '—'}</b></span>
          {data?.meta?.cached ? <><span>·</span><span>önbellekten</span></> : null}
        </div>
        <h1 className="mt-2 text-[32px] font-bold leading-tight tracking-tight text-slate-900">
          Kullanıcı sitede <span className="text-blue-700">nereye</span> gidiyor?
        </h1>
        <p className="mt-2 max-w-[70ch] text-[15px] leading-relaxed text-slate-600">
          {data?.meta?.host || 'Site'} içi gezinme haritası. Her düğüm gerçek bir sayfa, her şerit GA4’te ölçülmüş
          gerçek bir sayfa→sayfa geçiş. Aşağıdaki <b>Sayfa Gezgini</b>’nde bir sayfaya tıklayıp ondan sonra
          nereye gidildiğini adım adım takip edebilirsin.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {[7, 28, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`rounded-full border px-3.5 py-1.5 text-[12.5px] transition-colors ${
                days === d ? 'border-blue-600 bg-blue-50 font-semibold text-blue-700' : 'border-slate-300 text-slate-600 hover:border-blue-400'
              }`}
            >
              Son {d} gün
            </button>
          ))}
          <button
            type="button"
            onClick={() => load(true)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3.5 py-1.5 text-[12.5px] text-slate-600 hover:border-blue-400 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Yenile
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center gap-3 py-20 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          GA4’ten site içi geçişler çekiliyor — ilk yükleme 30-60 sn sürebilir.
        </div>
      ) : null}

      {data ? (
        <div className="flex flex-col gap-10">
          <section>
            <SectionHead eyebrow="01 — Ticari huni" title="Oturumdan siparişe" />
            <Funnel summary={data.summary} />
          </section>

          <section>
            <SectionHead eyebrow="02 — Giriş noktası" title="Oturumlar nerede başlıyor?" />
            <LandingSplit typeTotals={data.typeTotals} typeLabels={data.meta.typeLabels} />
          </section>

          <section>
            <SectionHead eyebrow="03 — Akış haritası" title="Sayfa tipleri arasında ileri akış">
              Şerit kalınlığı geçiş hacmini gösterir (doğrusal ölçek); kutu genişliği bir ölçek değil, sadece
              yerleşim. Burada yalnızca aşağı yönlü akış çizilir — geri dönüşler ve aynı katman içi gezinme
              hemen altındaki panellerde.
            </SectionHead>
            <FlowDiagram data={data} />
            <div className="mt-3 grid gap-3 lg:grid-cols-2">
              <EdgeList
                title="Geri dönüşler"
                note="Kullanıcının huniyi yukarı doğru geri kat ettiği geçişler."
                rows={backEdges}
                typeLabels={data.meta.typeLabels}
              />
              <EdgeList
                title="Aynı katman içinde gezinme"
                note="Aynı tipteki sayfalar arasında mekik dokuma — karşılaştırma davranışının ölçüsü."
                rows={selfEdges}
                typeLabels={data.meta.typeLabels}
              />
            </div>
          </section>

          <section>
            <SectionHead eyebrow="04 — Sayfa gezgini" title="Gerçek sayfa adlarıyla adım adım">
              Bir sayfa seç, sağda “buradan nereye gitti”, solda “buraya nereden geldi” listesini gör.
              Satıra tıklayınca o sayfaya geçer ve iz üstte birikir.
            </SectionHead>
            <Explorer data={data} adj={adj} />
          </section>

          {data.homeToCategory ? (
            <section>
              <SectionHead eyebrow="05 — Anasayfa çıkışı" title="Anasayfadan hangi kategoriye gidiliyor?">
                Anasayfadan sonraki adımı kategori sayfası olan geçişlerin <b>tamamı</b> — kırpılmadan, en çoktan
                en aza. Yüzde, anasayfadan kategoriye giden toplam hacme göre.
              </SectionHead>
              <div className="mb-3 flex flex-wrap gap-6 rounded-xl border border-slate-200 bg-white px-5 py-4">
                {[
                  ['Anasayfadan ölçülen çıkış', nf(data.homeToCategory.outTotal)],
                  ['Bunun kategoriye gideni', nf(data.homeToCategory.toCategoryTotal)],
                  ['Kategori payı', pct1(data.homeToCategory.toCategoryTotal, data.homeToCategory.outTotal || 1)],
                  ['Farklı kategori', nf(data.homeToCategory.count)],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{k}</div>
                    <div className="text-[19px] font-bold tabular-nums text-slate-900">{v}</div>
                  </div>
                ))}
              </div>
              <RankedList
                rows={homeCatRows}
                total={data.homeToCategory.toCategoryTotal}
                siteBase={data.meta.siteBase}
                valueLabel="Değer = anasayfadan o kategoriye ölçülen geçiş (görüntülenme)."
                emptyNote="Anasayfadan kategoriye ölçülmüş geçiş bulunamadı."
              />
            </section>
          ) : null}

          {data.categoryToCart ? (
            <section>
              <SectionHead eyebrow="06 — Kategoriden sepete" title="Hangi kategoriden en çok sepete gidiliyor?">
                Sitede <b>sepete ekleme olayı kategori sayfasında değil, ürün sayfasında</b> ateşleniyor
                ({nf(data.categoryToCart.productAtcTotal)} / {nf(data.categoryToCart.addToCartsAllPages)}). Bu yüzden
                iki farklı bakış var: ürünlerin sepete eklemesini geldikleri kategoriye paylaştıran <b>modellenmiş</b>
                {' '}görünüm, ve kategori sayfasından doğrudan sepet sayfasına yapılan <b>ölçülmüş</b> geçiş.
              </SectionHead>

              <div className="mb-3 flex flex-wrap items-center gap-2">
                {[
                  ['modeled', 'Modellenmiş — kategori → ürün → sepete ekleme'],
                  ['direct', 'Doğrudan ölçüm — kategori → sepet sayfası'],
                ].map(([k, lbl]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setCartMode(k)}
                    className={`rounded-full border px-3.5 py-1.5 text-[12.5px] transition-colors ${
                      cartMode === k ? 'border-blue-600 bg-blue-50 font-semibold text-blue-700' : 'border-slate-300 text-slate-600 hover:border-blue-400'
                    }`}
                  >
                    {lbl}
                  </button>
                ))}
              </div>

              <div className="mb-3 flex flex-wrap gap-6 rounded-xl border border-slate-200 bg-white px-5 py-4">
                {(cartMode === 'modeled'
                  ? [
                    ['Kategoriye atfedilen sepete ekleme', nf(data.categoryToCart.modeled.total)],
                    ['Ürün sayfalarındaki sepete ekleme', nf(data.categoryToCart.productAtcTotal)],
                    ['Atıf kapsamı', pct1(data.categoryToCart.modeled.total, data.categoryToCart.productAtcTotal || 1)],
                    ['Farklı kategori', nf(data.categoryToCart.modeled.count)],
                  ]
                  : [
                    ['Kategoriden sepete geçiş', nf(data.categoryToCart.direct.total)],
                    ['Farklı kategori', nf(data.categoryToCart.direct.count)],
                  ]
                ).map(([k, v]) => (
                  <div key={k}>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{k}</div>
                    <div className="text-[19px] font-bold tabular-nums text-slate-900">{v}</div>
                  </div>
                ))}
              </div>

              {cartMode === 'modeled' ? (
                <p className="mb-3 border-l-[3px] pl-3 text-[12.5px] leading-relaxed text-slate-600" style={{ borderColor: ALERT }}>
                  <b>Bu bir atıftır, ölçüm değildir.</b> Her ürünün sepete eklemesi, o ürüne gelen site içi trafiğin
                  kaynak dağılımına göre kategorilere paylaştırıldı (ürünün ATC’si × kategoriden gelen pay). Oturum
                  zinciri kurulmuyor. Kalan pay ürün→ürün, arama ve anasayfa gibi kategori olmayan kaynaklardan geliyor,
                  bu yüzden atıf kapsamı %100 değil.
                </p>
              ) : (
                <p className="mb-3 text-[12.5px] leading-relaxed text-slate-500">
                  Yalnızca kategori sayfasından sepet sayfasına yapılan gerçek sayfa geçişi. Küçük bir hacim —
                  ziyaretçilerin çoğu sepete ürün sayfasından ekliyor ve sepet sayfasına hiç uğramadan devam ediyor.
                </p>
              )}

              <RankedList
                rows={cartRows}
                total={cartMode === 'modeled' ? data.categoryToCart.modeled.total : data.categoryToCart.direct.total}
                siteBase={data.meta.siteBase}
                rightCol={(r) => `${(r.v > 0 ? (r.val / r.v) * 1000 : 0).toFixed(1).replace('.', ',')} ‰`}
                valueLabel={cartMode === 'modeled'
                  ? 'Değer = kategoriye atfedilen sepete ekleme. Sağdaki ‰ = 1.000 sayfa görüntülenmesi başına.'
                  : 'Değer = kategoriden sepet sayfasına geçiş. Sağdaki ‰ = 1.000 sayfa görüntülenmesi başına.'}
                emptyNote="Bu görünümde veri yok."
              />
            </section>
          ) : null}

          <section>
            <SectionHead eyebrow="07 — Tablo görünümü" title="En çok görüntülenen 25 sayfa">
              Grafiklerin arkasındaki ham sayılar. “Ölçülen çıkış”, o sayfadan sonraki adımı yakalanabilmiş
              görüntülenme sayısı.
            </SectionHead>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-[13.5px]">
                <thead>
                  <tr className="text-left text-[10.5px] uppercase tracking-wider text-slate-400">
                    <th className="px-4 py-2.5 font-medium">Sayfa</th>
                    <th className="px-4 py-2.5 font-medium">Tip</th>
                    <th className="px-4 py-2.5 text-right font-medium">Görüntülenme</th>
                    <th className="px-4 py-2.5 text-right font-medium">Ölçülen çıkış</th>
                    <th className="px-4 py-2.5 text-right font-medium">Kapsam</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.pages.slice(0, 25).map((p) => (
                    <tr key={p.p} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5">
                        <div className="text-slate-800">{p.l}</div>
                        <div className="font-mono text-[10.5px] text-slate-400">{p.p}</div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">{data.meta.typeLabels[p.t] || p.t}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-800">{nf(p.v)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-800">{nf(p.ko)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{pct1(p.ko, p.v || 1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <SectionHead eyebrow="08 — Yöntem" title="Bu veri nereden geliyor, sınırları ne?" />
            <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-5 text-[13.5px] leading-relaxed text-slate-600">
              <div>
                <h3 className="mb-1.5 flex items-center gap-1.5 font-semibold text-slate-800">
                  <Info className="h-4 w-4 text-slate-400" /> Kaynak
                </h3>
                <ul className="ml-5 list-disc space-y-1.5">
                  <li>
                    GA4 Data API, property <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[12px]">{data.meta.propertyId}</code>,{' '}
                    {dTR(data.meta.start)} – {dTR(data.meta.end)}. Geçişler{' '}
                    <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[12px]">pageReferrer</code> ×{' '}
                    <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[12px]">pagePath</code> boyut çiftinden,
                    giriş sayfaları <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[12px]">landingPage</code>’den.
                  </li>
                  <li>
                    Bu markada <b>BigQuery / GA4 ham export yok</b>, dolayısıyla GA4 arayüzündeki “Path exploration”
                    raporunun kaynağına erişimimiz yok. Bu harita onun Data API ile kurulabilen en yakın karşılığı.
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="mb-1.5 font-semibold text-slate-800">Normalizasyon</h3>
                <ul className="ml-5 list-disc space-y-1.5">
                  <li>Query string ve hash atıldı, <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[12px]">/tr</code> ile{' '}
                    <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[12px]">/tr/</code> tek düğüm sayıldı,
                    dil öneksiz gelen yollar <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[12px]">/tr/…</code> altına toplandı.
                  </li>
                  <li>
                    Kendine dönen geçişler (aynı sayfa → aynı sayfa) elendi: {nf(cov.selfLoopViews)} görüntülenme.
                    Bunlar filtre/sıralama/sayfalama yenilemesi, gerçek bir sayfa değişimi değil.
                  </li>
                  <li>Sayfa adları markanın <b>gerçek site menüsünden</b> alındı; menüde olmayan yollarda URL slug’ı okunur hale getirildi.</li>
                </ul>
              </div>
              <div className="border-l-[3px] pl-3" style={{ borderColor: ALERT }}>
                <h3 className="mb-1.5 flex items-center gap-1.5 font-semibold" style={{ color: ALERT }}>
                  <AlertTriangle className="h-4 w-4" /> Sınırlar — okurken dikkat
                </h3>
                <ul className="ml-5 list-disc space-y-1.5">
                  {cov.truncated ? (
                    <li>
                      API toplam <b>{nf(cov.edgeRowsTotal)}</b> ayrı geçiş satırı bildiriyor; bunun en büyük{' '}
                      <b>{nf(cov.edgeRowsFetched)}</b> tanesi çekildi. Uzun kuyruk eksik —{' '}
                      <b>oranlar güvenilir, mutlak toplamlar “en az bu kadar” olarak okunmalı.</b>
                    </li>
                  ) : null}
                  <li>
                    Ölçülebilen geçiş hacmi {nf(cov.measuredTransitionViews)}, toplam sayfa görüntülenme {nf(cov.allPageViews)}.
                    Aradaki fark oturumun bittiği, sekmenin kapandığı veya referrer’ın hiç gelmediği görüntülenmeler.
                  </li>
                  <li>
                    <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[12px]">pageReferrer</code> tarayıcının{' '}
                    <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[12px]">document.referrer</code>’ıdır. Tek sayfa
                    uygulaması geçişlerinde bayat kalabilir; buradaki sayılar <b>oturum içi sıralı yol</b> değil,{' '}
                    <b>sayfa çiftlerinin toplamı</b>.
                  </li>
                  <li>
                    Bir kullanıcının A→B→C yolunu birebir izlemiyoruz; A→B ve B→C ayrı ayrı sayılıyor. Gezgin’deki “iz”
                    bu yüzden bir <b>keşif aracı</b>, gerçek oturum kaydı değil.
                  </li>
                  <li>
                    <b>06’daki “modellenmiş” görünüm bir atıftır.</b> Sepete ekleme olayı ürün sayfasında ateşlendiği
                    için, her ürünün sepete eklemesi o ürüne gelen site içi trafiğin kaynak dağılımına göre kategorilere
                    paylaştırıldı. Tek bir kullanıcının “şu kategoriden girip şunu sepete attı” kaydı <b>yok</b>;
                    sıralama güvenilir, tek tek rakamlar tahmindir. Doğrudan ölçüm isteyen “kategori → sepet sayfası”
                    sekmesine baksın.
                  </li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
